'use client'
import { InfoIcon, XCircleIcon, WarningIcon, CheckCircleIcon } from '@phosphor-icons/react'
import React from 'react'

import { Toast, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport } from '@/components/ui/toast'
import { cn } from '@/lib/utils'

// Bottom center of the viewport; explicit `left-1/2 -translate-x-1/2` so the toast is not stuck to the main column’s left edge.
const centerViewportClasses =
  'fixed bottom-6 left-1/2 top-auto z-[100] flex w-[min(100vw-2rem,28rem)] max-w-none -translate-x-1/2 flex-col-reverse items-stretch p-0 pointer-events-none md:max-w-[min(100vw-2rem,28rem)]'

export function Toaster({
  isStoryBook,
  title,
  description,
  variant,
  classes,
  placement = 'default',
}: {
  isStoryBook?: boolean
  title: string
  description?: string
  variant: 'info' | 'error' | 'success' | 'warning'
  // `center` — bottom center of the screen; `default` — bottom-left stack
  placement?: 'default' | 'center'
  classes?: {
    toast?: string
    title?: string
    description?: string
    viewport?: string
  }
}) {
  const [open, setOpen] = React.useState(true)
  const timerRef = React.useRef(0)
  //These icons will be replaced with Phospor icons once thats set up
  const icons = {
    info: <InfoIcon size={24} weight="fill" className="fill-grey-500" />,
    error: <XCircleIcon size={24} weight="fill" className="fill-error-700" />,
    success: <CheckCircleIcon size={24} weight="fill" className="fill-success-700" />,
    warning: <WarningIcon size={24} weight="fill" className="fill-warning-700" />,
  }

  return (
    // We are unable to pass in class names or inline styles to the provider, and this gets rendered out in the document.
    // So we need to wrap the provider in a div and pass in the class names or inline styles to the div.
    <div className="absolute">
      <ToastProvider>
        {isStoryBook && (
          <button
            className="inline-flex h-9 items-center justify-center rounded border border-black bg-white px-4 text-base font-medium leading-9 outline-none"
            onClick={() => {
              setOpen(false)
              window.clearTimeout(timerRef.current)
              timerRef.current = window.setTimeout(() => {
                setOpen(true)
              }, 100)
            }}
          >
            Show Toast
          </button>
        )}

        <Toast open={open} onOpenChange={setOpen} className={cn(placement === 'center' && 'pointer-events-auto', classes?.toast)}>
          <div className="flex items-center justify-center gap-3">
            <span>{icons[variant]}</span>
            <div>
              {title && <ToastTitle className={classes?.title}>{title}</ToastTitle>}
              {description && <ToastDescription className={classes?.description}>{description}</ToastDescription>}
            </div>
          </div>
          <ToastClose />
        </Toast>

        <ToastViewport className={placement === 'center' ? cn(centerViewportClasses, classes?.viewport) : classes?.viewport} />
      </ToastProvider>
    </div>
  )
}
